/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useRef, useState } from 'react';

import ImageFallback from './ImageFallback';

interface Props {
  src: string;
  bemModifier: number | string;
  className?: string;
  showFallbackOnLoading?: boolean;
  fallbackClass?: string;
  fallbackText?: string;
  fallbackComponent?: JSX.Element;
  successClass?: string;
  onError?: () => void;
  onSuccess?: (imageRef: React.RefObject<HTMLImageElement>) => void;
}

export default function ImageLoader({
  src,
  bemModifier,
  className = '',
  showFallbackOnLoading = true, // HARDCODED to show fallback
  fallbackClass = '',
  fallbackText = '',
  fallbackComponent,
  successClass = '',
  onError,
  onSuccess,
}: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);

  const imageRef = useRef<HTMLImageElement>(null);

  const handleError = () => {
    if (onError) {
      onError();
    }

    setIsLoading(false);
    setLoadingError(true);
  };

  useEffect(() => {
    if (!onSuccess || isLoading || loadingError || !imageRef.current) {
      return;
    }
    onSuccess(imageRef);
  }, [isLoading, loadingError, onSuccess]);

  return (
    <div className={`image-loader-container ${className}`} id={`ImageLoader___${bemModifier}`}>
      {!loadingError ? (
        <img
          ref={imageRef}
          className={`image-loader ${!isLoading ? 'fadein' : ''} ${!isLoading && !loadingError ? successClass : ''}`}
          src={src}
          onLoad={() => setIsLoading(false)}
          onError={handleError}
          alt=""
          crossOrigin="anonymous"
          style={{ display: isLoading ? 'none' : undefined }}
          id={`ImageLoader_image___${bemModifier}`}
        />
      ) : null}

      <ImageFallback
        isLoading={isLoading}
        loadingError={loadingError}
        showFallbackOnLoading={showFallbackOnLoading}
        fallbackClass={fallbackClass}
        fallbackText={fallbackText}
        fallbackComponent={fallbackComponent}
        bemModifier={bemModifier}
      />
    </div>
  );
}
